import { UUID } from 'crypto';
import { createPortCallOrderChange } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/RotationPortCallChangeDataFactory';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { deepCopy } from 'src/utils/objectUtils';
import { isManualPortCall } from '../stowageRotationUtils';

export const onPortCallDragOver = (() => {
    let previousGuid;
    return (
        event: React.DragEvent<HTMLDivElement>,
        portCallItem: RotationPortCall,
        dragStartPortCall: React.MutableRefObject<RotationPortCall>,
        draggoverElement: React.MutableRefObject<HTMLDivElement>,
        dragoverPortCall: React.MutableRefObject<RotationPortCall>
    ) => {
        event.stopPropagation();
        event.preventDefault();
        if (previousGuid === portCallItem.portCallGuid) {
            return;
        }
        const targetParent = (event.target as HTMLDivElement).closest('.dragging-parent') as HTMLDivElement;
        if (isDraggingOverInvalidPortCall(dragStartPortCall, portCallItem)) {
            if (draggoverElement.current) {
                draggoverElement.current.classList.remove('dragging-over');
                draggoverElement.current = null;
            }
            if (dragoverPortCall.current) {
                dragoverPortCall.current = null;
            }
        } else if (targetParent) {
            setTimeout(() => {
                if (draggoverElement.current) {
                    draggoverElement.current.classList.remove('dragging-over');
                }
                targetParent.classList.add('dragging-over');
                draggoverElement.current = targetParent;
                dragoverPortCall.current = portCallItem;
                // TODO: PERFORMANCE - Remove debugging console.log from production code
                console.log('isDraggingOverInvalidPortCall - NOT - end');
            }, 0);
        }
        previousGuid = portCallItem.portCallGuid;
    };
})();

export const onDragoverParent = (
    event: React.DragEvent<HTMLDivElement>,
    portCallItem: RotationPortCall,
    dragStartPortCall: React.MutableRefObject<RotationPortCall>
) => {
    if (isDraggingOverInvalidPortCall(dragStartPortCall, portCallItem)) {
        event.dataTransfer.dropEffect = 'none';
    } else {
        event.dataTransfer.dropEffect = 'move';
    }
    event.stopPropagation();
    event.preventDefault();
};

export const onPortCallDragEnd = (
    event: React.DragEvent<HTMLDivElement>,
    portCallItem: RotationPortCall,
    stowagePlanGuid: UUID,
    filteredStowageRotationTree: RotationPortCall[],
    dragStartPortCall: React.MutableRefObject<RotationPortCall>,
    draggoverElement: React.MutableRefObject<HTMLDivElement>,
    dragoverPortCall: React.MutableRefObject<RotationPortCall>
) => {
    const stowagePlanChanges: StowagePlanChange[] = [];
    if (draggoverElement.current) {
        draggoverElement.current.classList.remove('dragging-over');
        draggoverElement.current = null;
    }
    if (dragStartPortCall.current) {
        dragStartPortCall.current = null;
    }

    if (dragoverPortCall.current) {
        // TODO: NAMING-CONVENTION - Variable names start with underscores (_originalPortCalls, _filteredStowageRotationTree) which is typically reserved for private/internal variables. Should use standard naming convention.
        const _originalPortCalls = deepCopy(filteredStowageRotationTree);
        const _filteredStowageRotationTree = [...filteredStowageRotationTree];
        const indexOfMoveTo = _filteredStowageRotationTree.findIndex((portCall) => portCall.portCallGuid === dragoverPortCall.current.portCallGuid);
        const indexOfMoveFrom = _filteredStowageRotationTree.findIndex((portCall) => portCall.portCallGuid === portCallItem.portCallGuid);
        const step = indexOfMoveFrom > indexOfMoveTo ? -1 : 1;

        if (isManualPortCall(portCallItem)) {
            // Added this special handling of manual port calls as a fix for bug report #30847.
            // If the moving port call is manual, we only adjust the manual port call's order number to +1/-1 of the dragover port call's order number, based on the direction of the drag.
            // This means we don't change non-manual port calls order numbers unnecessarily.
            stowagePlanChanges.push(
                createPortCallOrderChange(
                    stowagePlanGuid,
                    portCallItem.portCallGuid,
                    dragoverPortCall.current.portOrderNumber + step,
                    _originalPortCalls.find((pc) => pc.portCallGuid === portCallItem.portCallGuid)
                )
            );
        } else {
            const swapPortCalls = (i, j) => {
                const current = _filteredStowageRotationTree[i];
                const next = _filteredStowageRotationTree[j];

                [_filteredStowageRotationTree[i].portOrderNumber, _filteredStowageRotationTree[j].portOrderNumber] = [
                    next.portOrderNumber,
                    current.portOrderNumber,
                ];

                [_filteredStowageRotationTree[i], _filteredStowageRotationTree[j]] = [next, current];
            };

            for (let i = indexOfMoveFrom; i !== indexOfMoveTo; i += step) {
                swapPortCalls(i, i + step);
            }

            for (let i = indexOfMoveFrom; i !== indexOfMoveTo + step; i += step) {
                stowagePlanChanges.push(
                    createPortCallOrderChange(
                        stowagePlanGuid,
                        _filteredStowageRotationTree[i].portCallGuid,
                        _filteredStowageRotationTree[i].portOrderNumber,
                        // TODO: ARRAY-INDEX-SAFETY - Accessing _originalPortCalls[i] without bounds checking, should use find result directly
                        _originalPortCalls.find((pc) => pc.portCallGuid === _originalPortCalls[i].portCallGuid)
                    )
                );
            }
        }

        dragoverPortCall.current = null;
    }

    const target = event.target as HTMLDivElement;
    target.classList.remove('dragging');

    return stowagePlanChanges;
};

export const onPortCallDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    portCallItem: RotationPortCall,
    dragStartPortCall: React.MutableRefObject<RotationPortCall>
) => {
    event.stopPropagation();

    const targetElement = event.target as HTMLElement;
    const firstChild = targetElement.firstElementChild;

    if (firstChild && firstChild.classList.contains('open-port-node')) {
        firstChild.classList.remove('open-port-node');
    }

    dragStartPortCall.current = portCallItem;
    setTimeout(() => targetElement.classList.add('dragging'), 0);
};

const isDraggingOverInvalidPortCall = (dragStartPortCall: React.MutableRefObject<RotationPortCall>, portCallItem: RotationPortCall) => {
    const isSamePortCall = dragStartPortCall.current?.portCallGuid === portCallItem.portCallGuid;
    const isDifferentVoyage = portCallItem.voyageNumber !== dragStartPortCall.current?.voyageNumber;

    return isSamePortCall || isDifferentVoyage;
};
